import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'theme';
const PAGE_BG: Record<Theme, string> = { light: '#f8fafc', dark: '#0f0f23' };

/** Mirrors the pre-mount bootstrap script in public/index.html. */
const readStoredTheme = (): Theme => {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
    } catch {
        // Storage blocked (private mode, third-party cookie policy) — the
        // bootstrap script fell back to dark, so match it.
        return 'dark';
    }
};

type ThemeContextValue = {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Owns the theme for the whole app. It used to live in local state inside
 * <Header>, which meant every Header instance kept its own copy and any
 * screen that hid the Header lost the control entirely.
 */
export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const [theme, setTheme] = useState<Theme>(readStoredTheme);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        // Keep the paint-blocking background and the browser chrome colour in
        // step with the bootstrap script, so a reload never flashes the other
        // theme.
        document.documentElement.style.backgroundColor = PAGE_BG[theme];
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', PAGE_BG[theme]);
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // Preference stays in memory for this session rather than throwing.
        }
    }, [theme]);

    // Keep multiple tabs consistent.
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key !== STORAGE_KEY) return;
            setTheme(e.newValue === 'light' ? 'light' : 'dark');
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const toggleTheme = useCallback(
        () => setTheme(prev => (prev === 'dark' ? 'light' : 'dark')),
        [],
    );

    const value = useMemo<ThemeContextValue>(
        () => ({ theme, setTheme, toggleTheme }),
        [theme, toggleTheme],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used inside a ThemeProvider');
    return ctx;
};
